import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, MailCheck, MessageCircle } from "lucide-react";
import { LOGO_URL } from "../config";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-luxury-surface border border-luxury-border text-luxury-bone px-4 py-3.5 rounded-xl focus:outline-none focus:border-luxury-gold";

export default function JoinPage() {
  const { courseId } = useParams();
  const settings = useSite();
  const { lang } = useLang();
  const ar = lang === "ar";
  const [course, setCourse] = useState(undefined);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [state, setState] = useState("form"); // form | sent | denied

  useEffect(() => {
    axios
      .get(`${API}/courses`)
      .then((res) => {
        const found = (Array.isArray(res.data) ? res.data : []).find((c) => c.id === courseId);
        setCourse(found || null);
      })
      .catch(() => setCourse(null));
  }, [courseId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/join`, { email: email.trim(), course_id: courseId, origin_url: window.location.origin });
      setState("sent");
    } catch (err) {
      if (err.response?.status === 403) setState("denied");
      else setState("denied");
    } finally {
      setSending(false);
    }
  };

  if (course === undefined) {
    return <div className="min-h-screen bg-luxury-black flex items-center justify-center"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }
  if (!course) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4">
        <div className="text-center">
          <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-6" />
          <p className="text-luxury-bone font-serif text-3xl mb-6">{ar ? "الصفحة غير موجودة" : "Page not found"}</p>
          <Link to="/" className="text-luxury-gold hover:underline">{ar ? "العودة للرئيسية" : "Back home"}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-black hero-vignette flex items-center justify-center px-4 py-16" data-testid="join-page">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-luxury-surface border border-luxury-border rounded-2xl p-8 md:p-10 text-center"
      >
        <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-6" />
        {course.image ? (
          <img src={resolveMedia(course.image)} alt="" className="w-full h-40 object-cover rounded-xl mb-6" />
        ) : null}
        <p className="text-luxury-gold text-xs tracking-[0.3em] mb-3">{ar ? "برنامج خاص بالمشتركين" : "MEMBERS-ONLY PROGRAM"}</p>
        <h1 data-testid="join-course-title" className="font-serif font-bold text-3xl sm:text-4xl text-luxury-bone leading-snug mb-4">
          {pick(lang, course, "title")}
        </h1>

        {state === "form" && (
          <>
            <p className="text-luxury-gray text-sm leading-relaxed mb-8">
              {ar
                ? "هذا البرنامج مخصّص للمشتركين المسجّلين فقط. أدخل/أدخلي بريدك المسجّل ليصلك رابط الدخول الفوري."
                : "This program is for registered members only. Enter your registered email to receive your instant access link."}
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="join-form">
              <input
                data-testid="join-email-input"
                type="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={ar ? "بريدك الإلكتروني المسجّل" : "Your registered email"}
                className={inputCls}
                required
              />
              <button
                data-testid="join-submit-button"
                type="submit"
                disabled={sending}
                className="w-full bg-luxury-gold text-luxury-black font-semibold py-4 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {ar ? "أرسل/أرسلي لي رابط الدخول" : "Send my access link"}
              </button>
            </form>
          </>
        )}

        {state === "sent" && (
          <div data-testid="join-sent-message">
            <MailCheck className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-2xl mb-3">{ar ? "رابط الدخول في طريقه إليك" : "Your access link is on its way"}</p>
            <p className="text-luxury-gray text-sm">
              {ar ? "تفقّد/تفقّدي بريدك الوارد (والبريد غير الهام) — اضغط/اضغطي الرابط لتبدأ/تبدئي المشاهدة فوراً." : "Check your inbox (and spam folder) — tap the link to start watching instantly."}
            </p>
          </div>
        )}

        {state === "denied" && (
          <div data-testid="join-denied-message">
            <Lock className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-2xl mb-3">{ar ? "هذا البريد غير مسجّل" : "Email not registered"}</p>
            <p className="text-luxury-gray text-sm mb-6">
              {ar
                ? "هذا البريد الإلكتروني غير مسجّل في هذا البرنامج. إذا كنت مشتركاً/مشتركة، تأكد/تأكدي من استخدام نفس البريد الذي سجّلتِ به — أو تواصل/تواصلي معنا."
                : "This email is not registered for this program. If you're a member, make sure to use the same email you enrolled with — or contact us."}
            </p>
            <a
              data-testid="join-whatsapp-link"
              href={waLink(settings, undefined, lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold text-sm font-semibold px-6 py-3 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              {ar ? "تواصل/تواصلي معنا" : "Contact us"}
            </a>
          </div>
        )}
      </motion.div>
    </div>
  );
}
